import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

interface Cycle { id: number; name: string; year: number; status: string; participants_included?: number; participants_total?: number; }

const statusLabel: Record<string, string> = { draft: 'טיוטה', active: 'פעיל', closed: 'סגור' };
const statusClass: Record<string, string> = { draft: 'badge-pending', active: 'badge-active', closed: 'badge-closed' };

export default function AdminCycles() {
  const navigate = useNavigate();
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', year: new Date().getFullYear() });
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/cycles/').then(({ data }) => setCycles(data));
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await api.post('/cycles/', form);
      setShowModal(false);
      await load();
    } finally { setSaving(false); }
  };

  const handleActivate = async (id: number) => {
    if (!window.confirm('הפעלת התהליך תיצור את השאלונים לכל המשתתפים המסומנים בתהליך זה, ותשלח לכל אחד/ת מהם מייל: מי שעדיין אין לו/ה סיסמה יקבל/תקבל קישור להגדרת סיסמה, והשאר הודעה שהתהליך יצא לדרך. להמשיך?')) return;
    const { data } = await api.post(`/cycles/${id}/activate`);
    await load();
    if (data?.emails) window.alert(`התהליך הופעל. נשלחו ${data.emails.setup} קישורים להגדרת סיסמה ו-${data.emails.started} הודעות "התהליך יצא לדרך".`);
  };

  const handleClose = async (id: number) => {
    if (!window.confirm('לסגור את התהליך? לא ניתן יהיה להגיש שאלונים לאחר מכן.')) return;
    await api.post(`/cycles/${id}/close`);
    await load();
  };

  return (
    <div className="page page-narrow" style={{ maxWidth: 800 }}>
      <div className="page-header">
        <h1>ניהול תהליכים</h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ תהליך חדש</button>
        </div>
      </div>

      {cycles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <h3>אין תהליכים עדיין</h3>
          <p>צור תהליך משוב חדש כדי להתחיל</p>
        </div>
      ) : (
        cycles.map(c => (
          <div key={c.id} className="card" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3>{c.name}</h3>
                <div className="text-muted text-sm" style={{ marginTop: '4px' }}>
                  {c.year}
                  {c.participants_total !== undefined && <> · {c.participants_included} מתוך {c.participants_total} משתתפים</>}
                </div>
              </div>
              <span className={`badge ${statusClass[c.status]}`}>{statusLabel[c.status]}</span>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {c.status === 'draft' && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/cycles/${c.id}/questionnaires`)}>
                    ✏️ ערוך שאלונים
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/cycles/${c.id}/participants`)}>
                    👥 משתתפים
                  </button>
                  <button className="btn btn-success btn-sm" onClick={() => handleActivate(c.id)}>
                    ▶ הפעל תהליך
                  </button>
                </>
              )}
              {c.status === 'active' && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/dashboard')}>
                    📊 לוח בקרה
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/cycles/${c.id}/participants`)}>
                    👥 משתתפים
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleClose(c.id)}>
                    ⏹ סגור תהליך
                  </button>
                </>
              )}
              {c.status === 'closed' && (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/dashboard')}>
                    📊 צפה בנתונים
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/cycles/${c.id}/participants`)}>
                    👥 משתתפים
                  </button>
                </>
              )}
            </div>
          </div>
        ))
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">תהליך חדש</div>
            <div className="form-group">
              <label className="form-label">שם התהליך</label>
              <input className="form-input" placeholder="לדוגמה: תהליך משוב והתפתחות 2026" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">שנה</label>
              <input className="form-input" type="number" value={form.year} onChange={e => setForm(p => ({ ...p, year: parseInt(e.target.value) }))} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>ביטול</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving || !form.name}>
                {saving ? 'יוצר...' : 'צור תהליך'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
